import type {JSX} from 'react'
import type {GuidType} from '@/values/generic-type-definitions'
import type {Tag} from './vessel-selector.types'
import VesselSelectorButtonStyled from './vessel-selector-button.styled'
import {Spinner} from '../loading/spinner'
import {Colors} from '../../theme/colours'
import {Icon} from '../media/icons/Icon'
import ANCHOR_ICON from '../../@assets/icons/anchor.svg'

interface VesselForSummary {
    location: GuidType
    description: string
    tags?: Tag[]
}

interface VesselSelectionSummaryProps {
    isAllSelected: boolean
    selectedVessels: VesselForSummary[]
    selectedTags: Tag[]
    placeholder?: string
    isLoading?: boolean
}

export function VesselSelectionSummary(props: VesselSelectionSummaryProps): JSX.Element {
    // Max number of tags to show before cutting off
    const maxItemsToShow = 3

    if (props.isLoading) {
        return (
            <VesselSelectorButtonStyled.CenteredSpan>
                <Spinner color={Colors.blue2} />
            </VesselSelectorButtonStyled.CenteredSpan>
        )
    }

    const prefix = (
        <>
            <VesselSelectorButtonStyled.FilterSummaryPrefix>
                <Icon src={ANCHOR_ICON} alt="anchor" size="sm" />
                <VesselSelectorButtonStyled.FilterSummarySpan>
                    Viewing:
                </VesselSelectorButtonStyled.FilterSummarySpan>
            </VesselSelectorButtonStyled.FilterSummaryPrefix>
        </>
    )

    if (props.isAllSelected) {
        return (
            <>
                {prefix}
                Whole Fleet
            </>
        )
    }

    if (props.selectedVessels.length === 0) {
        return (
            <>
                {prefix}
                {props.placeholder ?? 'Select vessel(s) to view data'}
            </>
        )
    }

    const allResultsToShow = [
        ...props.selectedTags.map((tag) => ({key: tag.identity, label: tag.name})),
        ...props.selectedVessels.map((vessel) => ({
            key: vessel.location,
            label: vessel.description,
        })),
    ]

    const visibleItems = allResultsToShow.slice(0, maxItemsToShow)
    const numResultsToHide = Math.max(0, allResultsToShow.length - maxItemsToShow)

    return (
        <>
            {prefix}
            {visibleItems.map((item, i) => (
                <VesselSelectorButtonStyled.FilterSummaryResult key={item.key}>
                    {item.label}
                    {i < visibleItems.length - 1 ? ',' : ''}
                </VesselSelectorButtonStyled.FilterSummaryResult>
            ))}
            {numResultsToHide > 0 ? (
                <VesselSelectorButtonStyled.FilterSummaryCount>
                    +{numResultsToHide} more
                </VesselSelectorButtonStyled.FilterSummaryCount>
            ) : null}
        </>
    )
}
